import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PostCreator = ({ onPublish }) => {
  const [caption, setCaption] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [scheduleDate, setScheduleDate] = useState('');
  const [isScheduled, setIsScheduled] = useState(false);

  const handleFileSelect = (e) => {
    const files = Array.from(e?.target?.files);
    const validFiles = files?.filter(file => {
      const isImage = file?.type?.startsWith('image/');
      const isVideo = file?.type?.startsWith('video/');
      const isValidSize = file?.size <= 50 * 1024 * 1024; // 50MB
      return (isImage || isVideo) && isValidSize;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]?.slice(0, 5));
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev?.filter((_, i) => i !== index));
  };

  const handlePublish = () => {
    if (!caption?.trim() && selectedFiles?.length === 0) return;

    const postData = {
      caption: caption?.trim(),
      files: selectedFiles,
      scheduledDate: isScheduled ? scheduleDate : null,
      publishedAt: isScheduled ? null : new Date()?.toISOString()
    };

    onPublish(postData);
    setCaption('');
    setSelectedFiles([]);
    setScheduleDate('');
    setIsScheduled(false);
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-card p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <Icon name="Store" size={24} color="var(--color-primary)" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Crear Publicación</h3>
          <p className="text-sm text-muted-foreground">Comparte contenido con tus clientes</p>
        </div>
      </div>
      <div className="space-y-4">
        <div>
          <textarea
            value={caption}
            onChange={(e) => setCaption(e?.target?.value)}
            placeholder="¿Qué quieres compartir hoy? Escribe sobre promociones, nuevos productos o novedades..."
            className="w-full min-h-[120px] px-4 py-3 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {caption?.length}/500 caracteres
          </p>
        </div>

        {selectedFiles?.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {selectedFiles?.map((file, index) => (
              <div key={index} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-muted">
                  {file?.type?.startsWith('image/') ? (
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Vista previa ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="Video" size={32} color="var(--color-muted-foreground)" />
                    </div>
                  )}
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-error text-error-foreground flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Eliminar archivo"
                >
                  <Icon name="X" size={14} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <label className="cursor-pointer">
            <input
              type="file"
              multiple
              accept="image/*,video/*"
              onChange={handleFileSelect}
              className="hidden"
              disabled={selectedFiles?.length >= 5}
            />
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors">
              <Icon name="Image" size={20} color="var(--color-primary)" />
              <span className="text-sm font-medium text-foreground">
                Agregar Fotos/Videos
              </span>
            </div>
          </label>

          <button
            onClick={() => setIsScheduled(!isScheduled)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors"
          >
            <Icon name="Calendar" size={20} color="var(--color-secondary)" />
            <span className="text-sm font-medium text-foreground">
              {isScheduled ? 'Cancelar Programación' : 'Programar'}
            </span>
          </button>
        </div>

        {isScheduled && (
          <Input
            type="datetime-local"
            label="Fecha y hora de publicación"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e?.target?.value)}
            min={new Date()?.toISOString()?.slice(0, 16)}
          />
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>Máximo 5 archivos, 50MB cada uno</span>
          </div>
          <Button
            variant="default"
            iconName="Send"
            iconPosition="right"
            onClick={handlePublish}
            disabled={!caption?.trim() && selectedFiles?.length === 0}
          >
            {isScheduled ? 'Programar Publicación' : 'Publicar Ahora'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostCreator;